import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AlertController, IonicModule } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule } from '@swimlane/ngx-datatable';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { AvatarModule } from 'src/app/components/avatar/avatar.module';
import { getEmailDomain } from 'src/app/utils/utils';
import { createDataSource, FetchDataRequest, FetchDataResponse } from 'src/directives/paginated-datatable.directive';
import { SharedDirectivesModule } from 'src/directives/shared-directives.module';
import { Profile, WhereClause } from 'src/models';
import { Account } from 'src/models/accounts';
import { AccountsService, AccountUserRole } from 'src/services/accounts.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { UsersService } from 'src/services/users.service';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    NgxDatatableModule,
    SharedDirectivesModule,
    IonicModule,
    FormsModule,
    AvatarModule,
    NgxTippyModule,
  ],
  selector: 'app-account-admins',
  templateUrl: './account-admins.component.html',
  styleUrls: ['./account-admins.component.scss'],
})
export class AccountAdminsComponent implements OnInit {
  @Input() account: Account;

  accountAdmins: Partial<AccountUserRole>[] = [];
  canEditAdmins = false;

  isLoading = false;
  ColumnMode = ColumnMode;

  sortField = 'user.name';
  sortDesc = false;
  limit = 25;
  searchText: string;

  selectedUser: Partial<Profile>;
  isSubmitting = false;

  fetchAccountAdmins = async ({
    limit = this.limit,
    order = [this.sortField],
    descending = this.sortDesc,
    cursor,
  }: FetchDataRequest): Promise<FetchDataResponse<Partial<AccountUserRole>>> => {
    const options: any = {
      limit,
      order,
      descending,
      cursor,
    };

    const originalOptions = { ...options };

    if (!this.searchText) {
      return await this.accountsService.listUserRoles(this.account.id, originalOptions);
    }

    const resultsNameMatch = await this.accountsService.listUserRoles(this.account.id, {
      ...originalOptions,
      where: [
        {
          field: 'user.name',
          operator: 'like',
          value: `%${this.searchText}%`,
        },
      ],
    });
    const resultsEmailMatch = await this.accountsService.listUserRoles(this.account.id, {
      ...originalOptions,
      where: [
        {
          field: 'user.email',
          operator: 'like',
          value: `%${this.searchText}%`,
        },
      ],
    });

    // Deduplicate by userId since the same user could match both name and email
    const uniqueUsers = new Map<string, Partial<AccountUserRole>>();

    for (const item of resultsNameMatch.data) {
      if (item.userId) {
        uniqueUsers.set(item.userId, item);
      }
    }

    for (const item of resultsEmailMatch.data) {
      if (item.userId) {
        uniqueUsers.set(item.userId, item);
      }
    }

    const deduplicatedData = Array.from(uniqueUsers.values());

    return {
      data: deduplicatedData,
      totalCount: deduplicatedData.length,
      cursors: {
        hasNext: resultsNameMatch.cursors.hasNext || resultsEmailMatch.cursors.hasNext,
        hasPrevious: resultsNameMatch.cursors.hasPrevious || resultsEmailMatch.cursors.hasPrevious,
      },
    };
  };

  accountAdminsDataSource = createDataSource(this.fetchAccountAdmins);

  get hasAdmins(): boolean {
    return (this.accountAdminsDataSource.lastResponse?.data?.length ?? 0) > 0;
  }

  constructor(
    private accountsService: AccountsService,
    public authService: AuthService,
    private msgSrvc: MessageService,
    private alertCtrl: AlertController,
    private searchablePopoverService: SearchablePopoverService,
    private usersService: UsersService,
  ) {}

  ngOnInit(): void {
    this.canEditAdmins = this.authService.isTenantOwner || this.authService.isTenantAdmin;

    this.loadAccountAdmins();
  }

  async loadAccountAdmins() {
    this.isLoading = true;

    await this.accountAdminsDataSource.firstPage();

    this.isLoading = false;
  }

  handleSearch(event: any) {
    this.searchText = event.target.value;
    this.loadAccountAdmins();
  }

  resetForm() {
    this.selectedUser = undefined;
  }

  isValidNewAdmin(): boolean {
    return Boolean(this.selectedUser?.email && this.selectedUser.email.includes('@'));
  }

  selectUser() {
    // Get account domains for filtering
    const accountDomains = (this.account.emailsAndDomains ?? []).map((d) => d.toLowerCase().trim());

    this.searchablePopoverService.showPopover({
      event,
      showImages: true,
      showSearch: true,
      selectedItemId: this.selectedUser?.id,
      callback: async (id: string, title: string, user: any) => {
        // user is the object we passed in loadData
        this.selectedUser = {
          id: user.id,
          name: user.name,
          email: user.email,
          title: title,
        };
      },
      dynamicCreationCallback: async (text: string) => {
        try {
          // addUserRole will automatically create the internal user if it doesn't exist
          // No need to call usersService.create() first - it only creates external users
          await this.accountsService.addUserRole(this.account.id, {
            userId: this.selectedUser?.id,
            email: text.trim(),
            role: 'admin',
          });

          // Reload the admin list and show success message
          await this.loadAccountAdmins();
          this.msgSrvc.show('Admin saved successfully');
          this.resetForm();
        } catch (error: any) {
          const errorMessage = error?.error?.errors?.[0]?.title || 'Failed to create user or add as admin';
          this.msgSrvc.showError(errorMessage);
        }
      },
      loadData: async (searchText: string) => {
        const where: WhereClause[] = [
          {
            field: 'email',
            operator: '!=',
            value: null,
          },
        ];

        // Determine which fields to search based on search text
        let searchFields: string[] = [];
        if (searchText?.trim().length) {
          searchFields.push('name');
          searchFields.push('email');
        }

        const result = await this.usersService.list({
          limit: 100,
          includeExternalUsers: true,
          where,
          order: ['name', 'email'],
          descending: false,
          searchText: searchText?.trim() || undefined,
          searchFields: searchFields.length > 0 ? searchFields : undefined,
        });

        const data = [];

        result.data.forEach((user) => {
          // Filter: only include users whose email matches account domains
          if (user.email) {
            const userEmailLower = user.email.toLowerCase().trim();
            const userEmailDomain = getEmailDomain(user.email)?.toLowerCase();
            const emailMatches = accountDomains.includes(userEmailLower);
            const domainMatches = userEmailDomain && accountDomains.includes(userEmailDomain);

            // Skip users whose email doesn't match any account domain
            if (!emailMatches && !domainMatches) {
              return;
            }
          }

          // Show email address as the display title
          const displayTitle = user.name?.trim() || user.email?.trim() || 'Unknown User';

          data.push({
            title: displayTitle,
            id: user.id,
            image: user.photo, // `image` is the property used by the searchable-popover component
            object: user,
          });
        });

        return data;
      },
    });
  }

  async submit() {
    if (!this.selectedUser?.email) {
      return;
    }

    this.isSubmitting = true;

    try {
      await this.accountsService.addUserRole(this.account.id, {
        userId: this.selectedUser.id,
        email: this.selectedUser.email,
        name: this.selectedUser.name || undefined,
        role: 'admin',
      });

      this.resetForm();
      await this.loadAccountAdmins();
      this.msgSrvc.show('Admin added successfully');
    } catch (error: any) {
      const errorMessage = error?.error?.message || 'Failed to add admin';
      this.msgSrvc.showError(errorMessage);
    } finally {
      this.isSubmitting = false;
    }
  }

  async removeAdmin(userId: string, userName?: string) {
    const alert = await this.alertCtrl.create({
      header: 'Remove admin?',
      subHeader:
        userName && userName.trim().length > 0
          ? `This will remove ${userName}'s admin access to this account.`
          : 'This will remove their admin access to this account.',
      buttons: [
        {
          text: 'Cancel',
        },
        {
          text: 'Remove',
          handler: async () => {
            try {
              await this.accountsService.removeUserRole(this.account.id, userId);
              this.msgSrvc.show('Admin removed successfully');
              await this.loadAccountAdmins();
            } catch (error) {
              this.msgSrvc.showError('Failed to remove admin');
            }
          },
        },
      ],
    });

    await alert.present();
  }
}
